import { cn } from '@seller-hermes/utils/cn';
import { Megaphone, X } from 'lucide-react';
import * as React from 'react';
import { useQuery } from 'react-query';

const CHANGELOG_FEED_URL = 'https://docs.abbiamolog.com/changelog.json';
const CHANGELOG_PAGE_URL = 'https://docs.abbiamolog.com/docs/changelog';
const COLLAPSED_KEY = 'changelog_widget_collapsed';
const SEEN_DATE_KEY = 'changelog_widget_seen_date';
const MAX_ENTRIES = 3;

type ChangelogEntry = {
  date: string;
  title: string;
  description: string;
  tags: string[];
  slug: string;
  url: string;
};

const MONTH_ABBR_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

function formatDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-');
  return yyyy && mm && dd
    ? `${Number(dd)} ${MONTH_ABBR_PT[Number(mm) - 1] ?? mm}`
    : iso;
}

function readStorage(key: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore (modo privado / storage cheio) */
  }
}

/**
 * Widget "Mudanças recentes" no rodapé do sidebar. Puxa as últimas entradas
 * do changelog da doc (feed público /changelog.json) e mostra as 3 mais
 * recentes. Fechável: minimiza pra uma barrinha "Novidades" com um pontinho
 * quando há entrada mais nova que a última vista (estado em localStorage).
 *
 * Se o feed falhar ou vier vazio, o widget some — nunca quebra a navbar.
 */
export function ChangelogWidget() {
  const { data: entries } = useQuery<ChangelogEntry[]>(
    ['changelog-feed'],
    async () => {
      const res = await fetch(CHANGELOG_FEED_URL);
      if (!res.ok) throw new Error('changelog feed unavailable');
      return res.json();
    },
    {
      staleTime: 1000 * 60 * 60,
      cacheTime: 1000 * 60 * 60 * 24,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const [collapsed, setCollapsed] = React.useState(
    () => readStorage(COLLAPSED_KEY) === '1',
  );
  const [seenDate, setSeenDate] = React.useState(() =>
    readStorage(SEEN_DATE_KEY),
  );

  const items = React.useMemo(
    () => (entries ?? []).slice(0, MAX_ENTRIES),
    [entries],
  );
  const latestDate = entries?.[0]?.date ?? '';
  const hasNew = Boolean(latestDate) && latestDate > seenDate;

  // Enquanto está aberto, o conteúdo está visível → marca como visto.
  React.useEffect(() => {
    if (!collapsed && latestDate && latestDate > seenDate) {
      setSeenDate(latestDate);
      writeStorage(SEEN_DATE_KEY, latestDate);
    }
  }, [collapsed, latestDate, seenDate]);

  const collapse = () => {
    setCollapsed(true);
    writeStorage(COLLAPSED_KEY, '1');
  };

  const expand = () => {
    setCollapsed(false);
    writeStorage(COLLAPSED_KEY, '0');
  };

  if (!entries || entries.length === 0) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={expand}
        className="text-muted-foreground hover:bg-muted group-data-[collapsible=icon]:hidden flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-xs"
      >
        <Megaphone className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left">Novidades</span>
        {hasNew && (
          <span className="bg-foreground h-1.5 w-1.5 shrink-0 rounded-full" />
        )}
      </button>
    );
  }

  return (
    <div className="bg-muted/40 group-data-[collapsible=icon]:hidden mb-1 rounded-lg border p-2">
      <div className="mb-1 flex items-center justify-between gap-x-2 px-1">
        <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
          Mudanças recentes
        </span>
        <div className="flex items-center gap-x-1">
          <a
            href={CHANGELOG_PAGE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground text-[10px] font-medium"
          >
            Changelog →
          </a>
          <button
            type="button"
            onClick={collapse}
            aria-label="Minimizar novidades"
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-5 w-5 items-center justify-center rounded"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="flex flex-col">
        {items.map((item, idx) => (
          <a
            key={item.slug}
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              'hover:bg-muted block rounded-md px-1 py-1.5',
              idx > 0 && 'border-border border-t',
            )}
          >
            <div className="flex items-center gap-x-1.5 text-[9.5px] uppercase tracking-wider">
              <span className="text-muted-foreground font-mono">
                {formatDate(item.date)}
              </span>
              {item.tags[0] && (
                <span className="bg-green-500/10 text-green-600 dark:text-green-400 rounded-full px-1 py-px text-[9px] font-semibold leading-tight">
                  {item.tags[0]}
                </span>
              )}
            </div>
            <div className="mt-0.5 line-clamp-2 text-xs font-semibold">
              {item.title}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default ChangelogWidget;
